import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import Typography from "@mui/material/Typography";
import { useState } from "react";
import { useArchiveUser } from "@/hooks/useUsers";
import type { User } from "@/interfaces/user";

interface ArchiveUserDialogProps {
  user: User | null;
  onClose: () => void;
}

export default function ArchiveUserDialog({ user, onClose }: ArchiveUserDialogProps) {
  const [error, setError] = useState<string | null>(null);

  const { mutate: archive, isPending } = useArchiveUser();

  const handleClose = () => {
    setError(null);
    onClose();
  };

  const handleArchive = () => {
    if (!user) return;

    setError(null);

    archive(user.id, {
      onSuccess: handleClose,
      onError: (err) => setError(err instanceof Error ? err.message : "Failed to archive user."),
    });
  };

  return (
    <Dialog
      open={!!user}
      onClose={handleClose}
      disableScrollLock
      maxWidth={false}
      slotProps={{
        paper: {
          sx: {
            borderRadius: "16px",
            border: "1px solid var(--color-smoke)",
            p: "20px",
            m: 2,
            width: 577,
            maxWidth: "calc(100vw - 32px)",
          },
        },
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "44px" }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "12px",
            width: "100%",
          }}
        >
          <Box
            sx={{
              width: 76,
              height: 76,
              borderRadius: "50%",
              bgcolor: "var(--color-clear-dust)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
            aria-hidden="true"
          >
            <WarningAmberIcon sx={{ fontSize: 28, color: "var(--color-dark-dust)" }} />
          </Box>

          <Typography
            sx={{
              fontFamily: "var(--font-crimson)",
              fontSize: "30px",
              fontWeight: 400,
              lineHeight: "normal",
              color: "var(--color-rodeo-black)",
              textAlign: "center",
            }}
          >
            Archive user?
          </Typography>

          <Typography
            sx={{
              fontFamily: "var(--font-lato)",
              fontSize: "16px",
              color: "var(--color-graphite)",
              textAlign: "center",
              lineHeight: "normal",
            }}
          >
            <strong>{user?.name}</strong> will be archived and lose access to the platform. This
            action cannot be undone from the UI.
          </Typography>

          {error && (
            <Typography
              sx={{
                mt: 1.5,
                fontSize: 13,
                color: "var(--color-good-wine)",
                bgcolor: "var(--color-good-wine-bg)",
                px: 1.5,
                py: 1,
                borderRadius: "var(--radius-btn)",
                width: "100%",
                textAlign: "center",
              }}
            >
              {error}
            </Typography>
          )}
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: "44px" }}>
          <Button
            onClick={handleClose}
            disableRipple
            sx={{
              fontFamily: "var(--font-lato)",
              fontSize: "11px",
              fontWeight: 700,
              letterSpacing: "1.98px",
              textTransform: "uppercase",
              color: "var(--color-dark-dust)",
              p: 0,
              minWidth: "unset",
              bgcolor: "transparent",
              flexShrink: 0,
              "&:hover": { bgcolor: "transparent", textDecoration: "underline" },
            }}
          >
            Cancel
          </Button>

          <Button
            disableElevation
            disabled={isPending}
            onClick={handleArchive}
            sx={{
              fontFamily: "var(--font-lato)",
              fontSize: "11px",
              fontWeight: 700,
              letterSpacing: "1.98px",
              textTransform: "uppercase",
              bgcolor: "var(--color-rodeo-black)",
              color: "white",
              borderRadius: "var(--radius-btn)",
              px: "20px",
              py: "10px",
              height: "34px",
              flexShrink: 0,
              "&:hover": { bgcolor: "#1a1a1a" },
            }}
          >
            {isPending ? "Archiving…" : "Archive"}
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
}
